/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './CartNotificationBlockStyles.module.scss';
import { Close, ExclamationCircle, InfoCircle } from '@az/starc-ui-icons';
import { Actionable, Icon } from '@az/starc-ui';
import { WarningIcon } from '@/components/Cart/CartItems/Item/components/PromoStatusBadge/WarningIcon';
import { CheckmarkCircleAlternate } from '@/components/AZCustomComponent/PasswordRequirement/passwordRequirementAssets/CheckmarkCircleAlternate';
import ContentContainer from '@/components/AZCustomComponent/ContentContainer';
import { CheckoutSmartLink } from '@/components/AZCustomComponent/NotificationBlock/CheckoutSmartLink';
import { useLabels } from '@/hooks/useLabels';

export type NotificationType = 'error' | 'warn' | 'info' | 'success';

type Props = {
  message: string | React.ReactNode;
  subMessage?: React.ReactNode | string;
  type: NotificationType;
  itemLevel?: boolean;
  scrollToNotification?: boolean;
  id?: string;
  customClasses?: string;
  notificationContainerClassName?: string;
  errorFont?: boolean;
  checkoutSmartLink?: boolean;
  smartLinkMessage?: string;
  withPadding?: boolean;
  hasCloseIcon?: boolean;
  disableLiveRegion?: boolean;
};

const labelMap = {
  labelAriaLabelCloseNotification: 'label_aria_label_close_notification',
};

export function CartNotificationBlock({
  message,
  subMessage = '',
  type,
  itemLevel = false,
  scrollToNotification = false,
  id,
  customClasses,
  notificationContainerClassName,
  errorFont = false,
  checkoutSmartLink,
  smartLinkMessage,
  withPadding = true,
  hasCloseIcon = false,
  disableLiveRegion = false,
}: Props) {
  const labels = useLabels(labelMap);
  const [open, setOpen] = useState(true);
  const alertRef = useRef<HTMLDivElement | null>(null);

  const alertRole = !disableLiveRegion && type === 'error' ? 'alert' : undefined;
  const ariaLive = !disableLiveRegion ? (type === 'error' ? 'assertive' : 'polite') : undefined;

  const ariaLabel = useMemo(() => {
    if (typeof message !== 'string') {
      return '';
    }

    const startTag = '<phone>';
    const endTag = '</phone>';

    const startIndex = message.indexOf(startTag);
    const endIndex = message.indexOf(endTag);

    if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) {
      return '';
    }

    const phoneNumber = message.substring(startIndex + startTag.length, endIndex);

    if (!phoneNumber.trim()) {
      return '';
    }

    return `${message.substring(0, startIndex).trim()} ${phoneNumber}`.trim();
  }, [message]);

  const isChrome = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  }, []);

  useEffect(() => {
    if (!scrollToNotification || !alertRef.current) {
      return;
    }
    alertRef.current.setAttribute('tabindex', '-1');
    alertRef.current.focus();
  }, [scrollToNotification]);

  const handleOnCloseClick = () => {
    setOpen(false);
  };

  if (!open) {
    return null;
  }

  return (
    <>
      {ariaLabel.length > 0 && !isChrome ? (
        <div
          ref={alertRef}
          tabIndex={-1}
          role={alertRole}
          aria-live={ariaLive}
          aria-atomic={!disableLiveRegion ? 'true' : undefined}
          className={cx(styles.notificationContainer, notificationContainerClassName)}
          aria-label={ariaLabel}
        >
          <ContentContainer
            type={undefined}
            withPadding={withPadding}
            withMargin={!itemLevel}
            className={cx([
              styles.notificationContainerCart,
              customClasses,
              { [styles.warn]: type === 'warn' },
              { [styles.error]: type === 'error' },
              { [styles.info]: type === 'info' },
              { [styles.success]: type === 'success' },
            ])}
            id="notificationAlert"
            data-testid="notificationAlert"
          >
            <div className={cx(styles.notificationMessage)}>
              {type === 'warn' ? (
                <Icon
                  svg={WarningIcon}
                  size={6}
                  color="warning"
                  attributes={{ 'aria-hidden': 'true' }}
                  className={styles.icons}
                />
              ) : type === 'error' ? (
                <Icon
                  attributes={{ 'aria-hidden': 'true' }}
                  size={6}
                  svg={ExclamationCircle}
                  color="error"
                  className={styles.icons}
                />
              ) : type === 'success' ? (
                <Icon
                  svg={CheckmarkCircleAlternate}
                  color={'success'}
                  size={6}
                  attributes={{ 'aria-hidden': 'true' }}
                  className={styles.icons}
                />
              ) : (
                <Icon
                  svg={InfoCircle}
                  color={'focus'}
                  size={6}
                  attributes={{ 'aria-hidden': 'true' }}
                  className={styles.icons}
                />
              )}
              <span
                id={id ? id : ''}
                className={cx([
                  checkoutSmartLink || errorFont ? styles.errorFont : '',
                  styles.messageContainer,
                ])}
              >
                {message}{' '}
                {checkoutSmartLink && <CheckoutSmartLink smartLinkMessage={smartLinkMessage} />}
              </span>{' '}
              {hasCloseIcon && (
                <Actionable
                  onClick={handleOnCloseClick}
                  tabIndex={0}
                  attributes={{ ariaLabel: labels.labelAriaLabelCloseNotification }}
                >
                  <Icon
                    svg={Close}
                    size={5}
                    attributes={{
                      style: { color: '#202124', margin: '6px' },
                    }}
                  />
                </Actionable>
              )}
            </div>
          </ContentContainer>
        </div>
      ) : (
        <div
          ref={alertRef}
          tabIndex={-1}
          role={alertRole}
          aria-live={ariaLive}
          aria-atomic={!disableLiveRegion ? 'true' : undefined}
          className={cx(styles.notificationContainer, notificationContainerClassName)}
        >
          <ContentContainer
            type={undefined}
            withPadding={withPadding}
            withMargin={!itemLevel}
            className={cx([
              styles.notificationContainerCart,
              customClasses,
              { [styles.warn]: type === 'warn' },
              { [styles.error]: type === 'error' },
              { [styles.info]: type === 'info' },
              { [styles.success]: type === 'success' },
            ])}
            id="notificationAlert"
            data-testid="notificationAlert"
          >
            <div className={cx(styles.notificationMessage)}>
              {type === 'warn' ? (
                <Icon
                  svg={WarningIcon}
                  size={6}
                  color="warning"
                  attributes={{ 'aria-hidden': 'true' }}
                  className={styles.icons}
                />
              ) : type === 'error' ? (
                <Icon
                  attributes={{ 'aria-hidden': 'true' }}
                  size={6}
                  svg={ExclamationCircle}
                  color="error"
                  className={styles.icons}
                />
              ) : type === 'success' ? (
                <Icon
                  svg={CheckmarkCircleAlternate}
                  color={'success'}
                  size={6}
                  attributes={{ 'aria-hidden': 'true' }}
                  className={styles.icons}
                />
              ) : (
                <Icon
                  svg={InfoCircle}
                  color={'focus'}
                  size={6}
                  attributes={{ 'aria-hidden': 'true' }}
                  className={styles.icons}
                />
              )}
              <span
                id={id ? id : ''}
                className={cx([
                  checkoutSmartLink || errorFont ? styles.errorFont : '',
                  styles.messageContainer,
                ])}
              >
                {message} {subMessage}
                {checkoutSmartLink && <CheckoutSmartLink smartLinkMessage={smartLinkMessage} />}
              </span>
              {hasCloseIcon && (
                <Actionable
                  onClick={handleOnCloseClick}
                  tabIndex={0}
                  attributes={{ ariaLabel: labels.labelAriaLabelCloseNotification }}
                >
                  <Icon
                    svg={Close}
                    size={5}
                    attributes={{
                      style: { color: '#202124', margin: '6px' },
                    }}
                  />
                </Actionable>
              )}
            </div>
          </ContentContainer>
        </div>
      )}
    </>
  );
}

export default CartNotificationBlock;
